import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Brain, AlertTriangle, Download, Cpu, Clock, MapPin, Loader2, Crosshair, ExternalLink,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUT_BADGE = {
  en_attente: "text-slate-600 bg-slate-100", en_cours: "text-violet-700 bg-violet-100",
  termine: "text-teal-700 bg-teal-100", erreur: "text-red-700 bg-red-100", annule: "text-amber-700 bg-amber-100",
};
const STATUT_LABEL = { en_attente: "En attente", en_cours: "En cours", termine: "Terminé", erreur: "Erreur", annule: "Annulé" };
const SCORE_LABELS = { score_malignite: "Score de malignité", densite_cellulaire: "Densité cellulaire", index_proliferation: "Index de prolifération" };
const scoreColor = (s) => (s >= 0.7 ? "text-red-600 bg-red-50" : s >= 0.4 ? "text-amber-600 bg-amber-50" : "text-teal-600 bg-teal-50");

export default function AnalyseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get(`/analyses/${id}`);
        setA(data);
        if (["en_attente", "en_cours"].includes(data.statut)) timer = setTimeout(load, 3000);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    };
    load();
    return () => timer && clearTimeout(timer);
  }, [id]);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(a.resultats_json, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a"); el.href = url; el.download = `analyse-${id}.json`; el.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!a) return null;
  const r = a.resultats_json;
  const meta = r?.metadonnees || {};

  return (
    <div className="space-y-5 max-w-4xl" data-testid="analyse-detail-page">
      <button onClick={() => navigate("/analyses")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="analyse-back">
        <ArrowLeft className="h-4 w-4" /> Analyses IA
      </button>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start justify-between">
        <div>
          <h1 className="font-head text-xl font-bold text-slate-900 flex items-center gap-2">
            <Brain className="h-5 w-5 text-violet-600" /> {a.modele_nom} <span className="text-sm font-normal text-slate-400">v{a.version_modele}</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Créée le {new Date(a.created_at).toLocaleString("fr-CA")}
            {a.date_fin ? ` · terminée le ${new Date(a.date_fin).toLocaleString("fr-CA")}` : ""}
            {a.duree_secondes != null ? ` · ${a.duree_secondes}s` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUT_BADGE[a.statut]}`} data-testid="analyse-statut">{STATUT_LABEL[a.statut]}</span>
          {a.lame_id && (
            <button onClick={() => navigate(`/wsi/${a.lame_id}`)} className="text-xs flex items-center gap-1 text-sky-600 hover:underline" data-testid="analyse-open-slide">
              <ExternalLink className="h-3.5 w-3.5" /> Ouvrir la lame
            </button>
          )}
        </div>
      </div>

      {a.statut === "erreur" && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">Erreur : {a.erreur_message}</div>
      )}

      {r && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(r.scores || {}).map(([k, v]) => (
              <div key={k} className={`rounded-xl p-4 ${scoreColor(v)}`} data-testid={`analyse-score-${k}`}>
                <div className="text-xs font-medium">{SCORE_LABELS[k] || k}</div>
                <div className="text-3xl font-bold mt-1">{Math.round(v * 100)}%</div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><Crosshair className="h-4 w-4" /> Régions d'intérêt ({(r.regions_interet || []).length})</div>
            <div className="space-y-1.5" data-testid="analyse-roi-list">
              {(r.regions_interet || []).length === 0 && <p className="text-xs text-slate-400">Aucune région au-dessus du seuil ({r.seuil_utilise})</p>}
              {(r.regions_interet || []).map((roi) => (
                <div key={roi.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-1.5">
                  <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-orange-500" /> {roi.label} · {roi.geometrie.type}</span>
                  <span className="font-mono font-semibold text-orange-600">{Math.round(roi.score * 100)}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm">
              <div className="font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><Cpu className="h-4 w-4" /> Métadonnées</div>
              <dl className="space-y-1 text-xs text-slate-600">
                <div className="flex justify-between"><dt>Tuiles analysées</dt><dd className="font-medium">{r.nb_tuiles_analysees}</dd></div>
                <div className="flex justify-between"><dt>Durée inférence</dt><dd className="font-medium">{meta.duree_inference_ms} ms</dd></div>
                <div className="flex justify-between"><dt>Périphérique</dt><dd className="font-medium">{meta.device}</dd></div>
                <div className="flex justify-between"><dt>Pipeline</dt><dd className="font-medium">{meta.version_pipeline}</dd></div>
                <div className="flex justify-between"><dt>Seuil utilisé</dt><dd className="font-medium">{r.seuil_utilise}</dd></div>
              </dl>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm">
              <div className="font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><Clock className="h-4 w-4" /> Piste d'audit</div>
              <dl className="space-y-1 text-xs text-slate-600">
                <div className="flex justify-between"><dt>Lancée par</dt><dd className="font-medium truncate max-w-[10rem]">{a.lance_par_nom}</dd></div>
                <div className="flex justify-between"><dt>Priorité</dt><dd className="font-medium">{a.priorite}</dd></div>
                <div className="flex justify-between gap-2"><dt>Empreinte entrée</dt><dd className="font-mono truncate max-w-[8rem]">{a.hash_entree}</dd></div>
                <div className="flex justify-between gap-2"><dt>Empreinte sortie</dt><dd className="font-mono truncate max-w-[8rem]">{a.hash_sortie}</dd></div>
                {a.ip && <div className="flex justify-between"><dt>IP</dt><dd className="font-medium">{a.ip}</dd></div>}
              </dl>
            </div>
          </div>

          <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 rounded-xl p-3" data-testid="analyse-disclaimer">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{r.avertissement}</span>
          </div>

          <button onClick={exportJson} className="flex items-center gap-2 text-sm border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50" data-testid="analyse-export">
            <Download className="h-4 w-4" /> Exporter resultats_json
          </button>
        </>
      )}
    </div>
  );
}
